import { useState } from 'react';
import type { AdminUser, DirectoryUser, OrgRole } from '../../api/client';
import { useMe } from '../../api/hooks';
import { useGrantRole, useRevokeRole, useSetActive, useUsers } from '../../api/users';
import { useDirectorySearch, useInvite } from '../../api/directory';

const ORG_ROLES: OrgRole[] = ['HR', 'ADMIN'];

function InviteBox() {
  const search = useDirectorySearch();
  const invite = useInvite();
  const [q, setQ] = useState('');
  const [results, setResults] = useState<DirectoryUser[]>([]);
  const [msg, setMsg] = useState<string | null>(null);
  const [searching, setSearching] = useState(false);

  const doSearch = async () => {
    setMsg(null);
    if (q.trim().length < 2) return;
    setSearching(true);
    try {
      setResults(await search(q.trim()));
    } catch (e) {
      setResults([]);
      setMsg(e instanceof Error ? e.message : 'Search failed');
    } finally {
      setSearching(false);
    }
  };

  return (
    <div className="panel" style={{ marginBottom: '1rem' }}>
      <h3>Invite an employee</h3>
      <p className="muted small">Search your directory and add one person so you can grant them a role or assign them.</p>
      <div className="assign-add">
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Name or email…"
          onKeyDown={(e) => { if (e.key === 'Enter') doSearch(); }} />
        <button className="btn-ghost" onClick={doSearch} disabled={searching}>{searching ? 'Searching…' : 'Search'}</button>
      </div>
      {msg && <p className="muted small" style={{ marginTop: '0.5rem' }}>{msg}</p>}
      {results.length > 0 && (
        <table className="table">
          <tbody>
            {results.map((u) => (
              <tr key={u.azureOid}>
                <td>{u.displayName}<div className="muted small">{u.email}</div></td>
                <td style={{ textAlign: 'right' }}>
                  <button className="btn-ghost" disabled={invite.isPending}
                    onClick={() => invite.mutate(u, { onError: (e) => setMsg(e instanceof Error ? e.message : 'Invite failed') })}>
                    Add
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

export default function Users() {
  const { data: me } = useMe();
  const { data: users, isLoading, error } = useUsers();
  const grant = useGrantRole();
  const revoke = useRevokeRole();
  const setActive = useSetActive();
  const [banner, setBanner] = useState<string | null>(null);
  const busy = grant.isPending || revoke.isPending || setActive.isPending;

  const toggleRole = (user: AdminUser, role: OrgRole) => {
    setBanner(null);
    const has = user.roles.includes(role);
    (has ? revoke : grant).mutate(
      { userId: user.id, role },
      { onError: (e) => setBanner(e instanceof Error ? e.message : 'Action failed') },
    );
  };
  const toggleActive = (user: AdminUser) => {
    setBanner(null);
    setActive.mutate({ userId: user.id, active: !user.active },
      { onError: (e) => setBanner(e instanceof Error ? e.message : 'Action failed') });
  };

  return (
    <div style={{ width: '100%', maxWidth: 860 }}>
      <InviteBox />
      <div className="panel">
        <h2>Users &amp; roles</h2>
        <p className="muted">Grant org-wide roles. Reviewer and Interviewer are assigned per position, not here.</p>
        {banner && <div className="banner-error">{banner}</div>}
        {isLoading && <p className="muted">Loading users…</p>}
        {error && <p className="error">Could not load users: {String(error)}</p>}
        {users && (
          <table className="table">
            <thead>
              <tr><th>User</th><th>Status</th>{ORG_ROLES.map((r) => <th key={r} className="center">{r === 'HR' ? 'HR' : 'Admin'}</th>)}</tr>
            </thead>
            <tbody>
              {users.map((u) => {
                const isSelf = u.id === me?.id;
                return (
                  <tr key={u.id} className={u.active ? '' : 'row-inactive'}>
                    <td>
                      <div className="user-name">{u.displayName} {isSelf && <span className="tag">you</span>}</div>
                      <div className="muted small">{u.email}</div>
                    </td>
                    <td>
                      <button className={u.active ? 'pill pill-on' : 'pill pill-off'} disabled={busy} onClick={() => toggleActive(u)}>
                        {u.active ? 'Active' : 'Inactive'}
                      </button>
                    </td>
                    {ORG_ROLES.map((role) => {
                      const has = u.roles.includes(role);
                      return (
                        <td key={role} className="center">
                          <input type="checkbox" checked={has} disabled={busy} onChange={() => toggleRole(u, role)}
                            aria-label={`${has ? 'Revoke' : 'Grant'} ${role} for ${u.email}`} />
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
